export const LIBRARY_PAGE_SIZES = [10, 30, 50] as const
export const LIBRARY_PAGE_SIZE_STORAGE_KEY = 'miosegno.library.pageSize'

export type LibraryPageSize = (typeof LIBRARY_PAGE_SIZES)[number]
export type LibraryPageItem = number | 'ellipsis-left' | 'ellipsis-right'

export function normalizeLibraryPageSize(value: unknown): LibraryPageSize {
  const numeric = Number(value)
  return numeric === 10 || numeric === 30 || numeric === 50 ? numeric : 30
}

export function buildLibraryPageItems(currentPage: number, totalPages: number): LibraryPageItem[] {
  if (totalPages <= 11) {
    return Array.from({ length: Math.max(0, totalPages) }, (_, index) => index + 1)
  }

  const pages = new Set<number>([1, totalPages])

  if (currentPage <= 6) {
    for (let page = 1; page <= 9; page += 1) pages.add(page)
  } else if (currentPage >= totalPages - 5) {
    for (let page = totalPages - 8; page <= totalPages; page += 1) pages.add(page)
  } else {
    for (let page = currentPage - 3; page <= currentPage + 3; page += 1) pages.add(page)
  }

  const sorted = [...pages]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((left, right) => left - right)
  const result: LibraryPageItem[] = []

  sorted.forEach((page, index) => {
    const previous = sorted[index - 1]
    if (index > 0 && previous !== undefined && page - previous > 1) {
      result.push(previous === 1 ? 'ellipsis-left' : 'ellipsis-right')
    }
    result.push(page)
  })

  return result
}

export function getLibraryPagination(
  totalItems: number,
  pageSize: LibraryPageSize,
  requestedPage: number,
) {
  const safeTotalItems = Math.max(0, totalItems)
  const totalPages = Math.max(1, Math.ceil(safeTotalItems / pageSize))
  const currentPage = Math.min(totalPages, Math.max(1, Math.trunc(requestedPage) || 1))
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = Math.min(startIndex + pageSize, safeTotalItems)

  return {
    currentPage,
    totalPages,
    startIndex,
    endIndex,
    startNumber: safeTotalItems === 0 ? 0 : startIndex + 1,
    endNumber: endIndex,
    items: buildLibraryPageItems(currentPage, totalPages),
  }
}
